/**
 * useFxRates Hook
 *
 * React Query hook for fetching live FX rates from the API.
 * Only fetches rates for currencies actually in use (on-demand).
 * Caches results for 5 minutes to reduce API calls.
 *
 * Uses marketDataService.getForexRates() -- single POST with all currencies
 * instead of N parallel GET requests (PWA-1180 migration).
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import marketDataService from "../services/marketDataService";

// ============================================================================
// Types
// ============================================================================

/**
 * FX rates lookup: currency code -> USD exchange rate
 */
export type FxRates = Record<string, number>;

export interface UseFxRatesResult {
  /** Map of currency code to USD exchange rate */
  fxRates: FxRates;
  /** Whether any rates are still loading */
  isLoading: boolean;
  /** Whether all rates have loaded successfully */
  isSuccess: boolean;
  /** First error encountered, if any */
  error: Error | null;
}

// ============================================================================
// Query Keys
// ============================================================================

export const fxRatesQueryKeys = {
  /** Single query key for all currencies (batched) */
  rates: (currencies: string[]) => ["fxRates", ...currencies.sort()] as const,
};

// ============================================================================
// Constants
// ============================================================================

/** Stale time for FX rate cache: 5 minutes */
const FX_RATE_STALE_TIME = 5 * 60 * 1000;

/** Cache time for FX rates: 10 minutes */
const FX_RATE_CACHE_TIME = 10 * 60 * 1000;

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook to fetch live FX rates for a set of currencies.
 *
 * @param currencies - Array of currency codes to fetch rates for
 * @returns FX rates object and loading/error state
 */
export function useFxRates(currencies: string[]): UseFxRatesResult {
  // Normalize and deduplicate currencies, filter out USD (rate is always 1)
  const uniqueCurrencies = useMemo(() => {
    const normalized = currencies.map(c => c.toUpperCase());
    return Array.from(new Set(normalized)).filter(c => c !== "USD");
  }, [currencies]);

  // Single batched query for all non-USD currencies
  const {
    data: forexRatesData,
    isLoading,
    isSuccess: querySuccess,
    error: queryError,
  } = useQuery({
    queryKey: fxRatesQueryKeys.rates(uniqueCurrencies),
    queryFn: () => marketDataService.getForexRates(uniqueCurrencies),
    enabled: uniqueCurrencies.length > 0,
    staleTime: FX_RATE_STALE_TIME,
    gcTime: FX_RATE_CACHE_TIME,
    retry: 2,
  });

  // Build the FxRates object from query result
  const fxRates = useMemo(() => {
    const rates: FxRates = {
      USD: 1.0, // USD is always 1
    };

    if (forexRatesData) {
      for (const [currency, entry] of Object.entries(forexRatesData)) {
        rates[currency.toUpperCase()] = entry.rate;
      }
    }

    return rates;
  }, [forexRatesData]);

  const isSuccess = querySuccess || uniqueCurrencies.length === 0;
  const error = queryError as Error | null;

  return {
    fxRates,
    isLoading,
    isSuccess,
    error,
  };
}

export default useFxRates;

